import React, { useEffect, useState } from 'react';
import { Loader2, Check, X, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { cn } from '../../lib/utils';

const fmtMoney = (v) => (v == null || Number.isNaN(v) ? '—' : `$${Math.round(v).toLocaleString()}`);

// Inline budget / impact editor for a programme card. Cost per LEI point
// recalculates live as you type (investment ÷ current overall LEI score).
export default function BudgetInlineEdit({ entity, isDark, onSaved, onClose }) {
  const [investment, setInvestment] = useState(entity.investment ?? '');
  const [impact, setImpact] = useState(entity.impact_per_percent ?? '');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  useEffect(() => {
    api.get(`/segments/programs/${entity.cohort_id}/budget-history?limit=3`)
      .then((r) => setHistory(r.data?.history || []))
      .catch(() => setHistory([]));
  }, [entity.cohort_id]);
  const score = Number(entity.overall_score) || 0;
  const inv = Number(investment);
  const liveCost = score > 0 && inv > 0 ? inv / score : null;

  const stop = (e) => e.stopPropagation();
  const save = async (e) => {
    stop(e);
    if (!(inv >= 0)) { toast.error('Budget must be a number'); return; }
    setSaving(true);
    try {
      const metadata = { total_investment: Math.round(inv) };
      if (impact !== '' && !Number.isNaN(Number(impact))) metadata.impact_per_percent = Math.round(Number(impact));
      await api.put(`/segments/programs/${entity.cohort_id}`, { metadata });
      toast.success(`${entity.cohort_name}: budget updated`);
      onClose?.();
      onSaved?.();
    } catch (err) { toast.error(`Save failed: ${err.response?.data?.detail || err.message}`); }
    finally { setSaving(false); }
  };

  const inputCls = cn('w-full rounded-md border px-2 py-1 text-xs tabular-nums outline-none', isDark ? 'bg-[#0b1220] border-[#22304d] text-slate-100' : 'bg-white border-slate-300 text-slate-800');
  return (
    <div onClick={stop} onKeyDown={stop} className={cn('w-full rounded-lg border p-2.5 space-y-2 cursor-default', isDark ? 'border-cyan-500/30 bg-[#0b1220]' : 'border-teal-200 bg-white')} data-testid={`program-budget-form-${entity.cohort_id}`}>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className={cn('block text-[10px] uppercase tracking-wide mb-0.5', isDark ? 'text-slate-400' : 'text-slate-500')}>Budget ($)</span>
          <input type="number" min="0" step="500" value={investment} onChange={(e) => setInvestment(e.target.value)} className={inputCls} autoFocus data-testid={`program-budget-input-${entity.cohort_id}`} />
        </label>
        <label className="block">
          <span className={cn('block text-[10px] uppercase tracking-wide mb-0.5', isDark ? 'text-slate-400' : 'text-slate-500')}>Impact / LEI pt ($)</span>
          <input type="number" min="0" step="100" value={impact} onChange={(e) => setImpact(e.target.value)} className={inputCls} data-testid={`program-impact-input-${entity.cohort_id}`} />
        </label>
      </div>
      <div className="flex items-center justify-between gap-2">
      {history && history.length > 0 && (
        <ul className={cn('border-t pt-1.5 space-y-0.5', isDark ? 'border-[#22304d]' : 'border-slate-100')} data-testid={`program-budget-history-${entity.cohort_id}`}>
          {history.map((h) => (
            <li key={h.id} className={cn('flex items-center gap-1.5 text-[10px]', isDark ? 'text-slate-400' : 'text-slate-500')}>
              <History className="h-3 w-3 shrink-0 opacity-70" />
              <span className="truncate">
                <b className={isDark ? 'text-slate-300' : 'text-slate-700'}>{h.changed_by}</b> · {new Date(h.changed_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} · {fmtMoney(h.before?.total_investment)} → {fmtMoney(h.after?.total_investment)}
                {h.before?.impact_per_percent !== h.after?.impact_per_percent && <> · impact {fmtMoney(h.before?.impact_per_percent)} → {fmtMoney(h.after?.impact_per_percent)}</>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {history && history.length === 0 && <p className={cn('text-[10px]', isDark ? 'text-slate-500' : 'text-slate-400')} data-testid={`program-budget-history-empty-${entity.cohort_id}`}>No budget changes recorded yet.</p>}

        <span className={cn('text-[11px]', isDark ? 'text-slate-300' : 'text-slate-600')}>Cost / LEI pt <b className="tabular-nums" data-testid={`program-budget-live-cost-${entity.cohort_id}`}>{fmtMoney(liveCost)}</b> <span className="opacity-60">at {score.toFixed(1)} LEI</span></span>
        <div className="flex gap-1">
          <span role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") { stop(e); onClose?.(); } }} onClick={(e) => { stop(e); onClose?.(); }} className={cn('inline-flex items-center gap-1 text-[11px] font-semibold rounded-md border px-2 py-1 cursor-pointer', isDark ? 'border-[#22304d] text-slate-300' : 'border-slate-200 text-slate-600')} data-testid={`program-budget-cancel-${entity.cohort_id}`}><X className="h-3 w-3" /> Cancel</span>
          <span role="button" tabIndex={0} aria-disabled={saving} onKeyDown={(e) => { if (e.key === "Enter" && !saving) save(e); }} onClick={(e) => { if (!saving) save(e); }} className={cn("inline-flex items-center gap-1 text-[11px] font-semibold rounded-md bg-teal-600 text-white px-2 py-1 hover:bg-teal-700 cursor-pointer", saving && "opacity-50 pointer-events-none")} data-testid={`program-budget-save-${entity.cohort_id}`}>{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save</span>
        </div>
      </div>
    </div>
  );
}
